<template>
    <b-row class="p-4">
        <b-col cols="12" md="8" class="mb-4">
            <div class="map-container" ref="mapContainer">
                <div
                    ref="nedglobeMap"
                    id="nedglobe.map"
                    class="map"
                    tabindex="0"
                    aria-label="Kaart"
                    role="application"
                ></div>
            </div>
        </b-col>
        <b-col cols="12" md="4">
            <map-controls />
        </b-col>
    </b-row>
</template>

<script>
    import { mapActions, mapGetters, mapState } from 'vuex';
    import OlClusterSource from 'ol/source/Cluster';
    import { debounce, throttle } from 'lodash';
    import Select from 'ol/interaction/Select';

    import {
        MAP_INTERACTION_TIMEOUTS,
        INTERFACE_PANEL_TRIGGERS,
        PROJECTION_AMERSFOORT_RD_BOUNDS
    } from '../../../constants';

    import MapControls from '../panels/accessible/MapControls.vue';

    export default {
        components: { 'map-controls': MapControls },

        props: ['tma'],

        data() {
            return { map: null, storeSubscribtion: null };
        },

        async mounted() {
            this.$store.dispatch('trackUserEvent', {
                name: 'accessible',
                stage: 'open'
            });
            this.init();
        },

        async destroyed() {
            this.storeUnSubscribe && this.storeUnSubscribe();
        },

        computed: {
            ...mapGetters([
                'getWebTheme',
                'getOrganizationName',
                'getWebThemeParameters',
                'getWebThemeHasFeedbackType'
            ]),
            ...mapState(['uiElements']),
            informationPanel() {
                return this.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION];
            },
            webTheme() {
                return this.getWebTheme?.data;
            }
        },
        watch: {
            'informationPanel.loading': function (newVal) {
                this.$store.dispatch('setLoader', { loading: newVal });
            },
            $route() {
                this.resizeMap();
            }
        },

        methods: {
            ...mapActions(['startNedGlobe', 'togglePanel']),
            async init() {
                let webThemeParameters = { tma: this.tma };
                // Check the webtheme ID and request it if a guid was in the URL
                webThemeParameters = await this.$store.dispatch('checkTmaId', {
                    webThemeParameters
                });

                if (webThemeParameters.tma === 0) {
                    this.$router.push('/error/webtheme_unknown');
                }
                const mapConfig = await this.startNedGlobe({
                    mapElementId: 'nedglobe.map',
                    webThemeParameters,
                    accessibleMode: true
                });
                this.map = mapConfig.map;
                this.storeUnSubscribe = this.$store.subscribe((mutation) => {
                    this.$store.dispatch('updateMainMap', { mutation });
                });
                this.$_listenToMap(mapConfig);
                this.$_setDocumentTitle();
                this.$_checkCenter();
            },
            $_listenToMap(mapConfig) {
                const { dispatch, commit } = this.$store;
                const mapName = mapConfig.name;
                const map = mapConfig.map;
                const longHold = MAP_INTERACTION_TIMEOUTS.longHold;
                const debounceDefaultWaitMs =
                    MAP_INTERACTION_TIMEOUTS.debounceDefaultWaitMs;

                map.once('postrender', () => {
                    const selectCluster = new Select({
                        style: null,
                        layers: (layer) => {
                            return layer.getSource() instanceof OlClusterSource;
                        }
                    });
                    selectCluster.on('select', function (e) {
                        if (e.selected.length > 0) {
                            commit('MAP_SELECT_CLUSTER', {
                                mapName,
                                features: e.selected,
                                coordinate: e.coordinate
                            });
                            selectCluster.getFeatures().clear();
                        }
                    });

                    map.on(
                        'moveend',
                        debounce(() => {
                            dispatch('mapMoveEnd', { mapName });
                        }, debounceDefaultWaitMs)
                    );

                    map.on(
                        'pointerdrag',
                        debounce(() => {
                            dispatch('mapPointerdrag', { mapName });
                        }, debounceDefaultWaitMs)
                    );

                    // On Map click
                    map.on('click', (event) => {
                        // give some time to record cluster click
                        if (this.getWebThemeHasFeedbackType) {
                            setTimeout(() => {
                                dispatch('mapClicked', { mapName, event });
                            }, longHold);
                        } else {
                            dispatch('mapClicked', { mapName, event });
                        }
                    });

                    map.addInteraction(selectCluster);

                    // On Zoom change
                    map.getView().on(
                        'change:resolution',
                        throttle(
                            () => {
                                dispatch('mapResolutionChanged', { mapName });
                            },
                            300,
                            { leading: false, trailing: true }
                        )
                    );
                });
            },
            $_setDocumentTitle() {
                let suffix = this.webTheme.portalGroup;
                if (!suffix || suffix === '') {
                    suffix = this.getOrganizationName;
                }
                document.title = `${this.webTheme.alias} - ${suffix}`;
            },
            $_checkCenter() {
                if (this.$_invalidCenter(this.getWebThemeParameters.center)) {
                    this.$store.commit('SET_ALERT_MESSAGE', {
                        message: `${this.$t(
                            'errorWebthemeInvalidCenter'
                        )} \n ${this.$t('contactAdminForUrl')}`,
                        alertClass: null,
                        variant: 'danger',
                        optional: false,
                        hasSearch: this.webTheme.showSearch
                    });
                }
            },
            /**
             * Check if center is within bounds of Amersfoort RD extent
             * @param center
             */
            $_invalidCenter(center) {
                if (center) {
                    const centerX = center[0];
                    const centerY = center[1];
                    if (
                        centerX < PROJECTION_AMERSFOORT_RD_BOUNDS.minX ||
                        centerX > PROJECTION_AMERSFOORT_RD_BOUNDS.maxX ||
                        centerY < PROJECTION_AMERSFOORT_RD_BOUNDS.minY ||
                        centerY > PROJECTION_AMERSFOORT_RD_BOUNDS.maxY
                    ) {
                        return true;
                    }
                }
                return false;
            },
            resizeMap() {
                const { width, height } =
                    this.$refs.mapContainer.getBoundingClientRect();
                this.map?.setSize([width, height]);
            }
        }
    };
</script>

<style scoped lang="scss">
.map-container {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 70vh;
}
</style>
